<script setup lang="ts">
import type { RegistrationAttendeeResponse } from '@@/types/api';

const props = defineProps<{
  attendees: RegistrationAttendeeResponse[];
  isLoading: boolean;
  isInviting: boolean;
  isRescinding: boolean;
  inviteError: string | null;
  canInvite: boolean;
}>();

const emit = defineEmits<{
  'invite-submit': [email: string];
  rescind: [attendeeId: string];
  'clear-error': [];
}>();

const invitingSeatId = ref<string | null>(null);
const inviteForm = ref<{ reset: () => void } | null>(null);

function startInvite(seatId: string) {
  emit('clear-error');
  invitingSeatId.value = seatId;
}

function cancelInvite() {
  invitingSeatId.value = null;
  inviteForm.value?.reset();
  emit('clear-error');
}

async function handleSubmit(email: string) {
  emit('invite-submit', email);
}

// Close invite form once the loading state finishes successfully
watch(
  () => props.isInviting,
  (isInviting, wasInviting) => {
    if (wasInviting && !isInviting && !props.inviteError) {
      invitingSeatId.value = null;
      inviteForm.value?.reset();
    }
  },
);

function seatLabel(attendee: RegistrationAttendeeResponse, index: number) {
  if (attendee.is_registrant) return 'You';
  return `Seat ${index + 1}`;
}
</script>

<template>
  <div class="party-roster-card">
    <div class="text-subtitle-2 font-weight-bold mb-2">Your party</div>

    <v-skeleton-loader v-if="isLoading" type="list-item" class="mb-2" />

    <template v-else>
      <div
        v-for="(attendee, index) in attendees"
        :key="attendee.id"
        class="d-flex align-center pa-2 mb-2 rounded-lg bg-surface-variant"
      >
        <v-icon size="small" class="mr-2">
          {{
            attendee.is_registrant
              ? 'mdi-account-check'
              : attendee.invite_status === 'claimed'
                ? 'mdi-account-check'
                : attendee.invite_status === 'invited'
                  ? 'mdi-email-outline'
                  : 'mdi-account-outline'
          }}
        </v-icon>

        <div class="flex-grow-1">
          <div class="text-body-2 font-weight-medium">
            {{ seatLabel(attendee, index) }}
          </div>
          <div
            v-if="attendee.user && attendee.invite_status === 'claimed'"
            class="text-caption text-medium-emphasis"
          >
            {{ attendee.user.name }}
          </div>
          <div
            v-else-if="attendee.invite_status === 'invited'"
            class="text-caption text-medium-emphasis"
          >
            Invited: {{ attendee.invite_email }}
          </div>
          <div v-else class="text-caption text-medium-emphasis">Empty</div>
        </div>

        <!-- Rescind button for invited (unclaimed) seats -->
        <v-btn
          v-if="attendee.invite_status === 'invited' && canInvite"
          icon="mdi-close"
          size="x-small"
          variant="text"
          :loading="isRescinding"
          :title="`Rescind invite for ${attendee.invite_email}`"
          @click="emit('rescind', attendee.id)"
        />

        <!-- Invite button for empty seats -->
        <v-btn
          v-else-if="
            !attendee.is_registrant &&
            !attendee.invite_status &&
            canInvite &&
            invitingSeatId !== attendee.id
          "
          size="x-small"
          variant="outlined"
          color="primary"
          @click="startInvite(attendee.id)"
        >
          Invite
        </v-btn>
      </div>

      <!-- Inline invite form -->
      <div
        v-if="invitingSeatId"
        class="pa-3 mb-2 rounded-lg"
        style="border: 1px solid rgba(var(--v-border-color), 0.4)"
      >
        <EventsInviteAttendeeForm
          ref="inviteForm"
          :is-loading="isInviting"
          :error="inviteError"
          @submit="handleSubmit"
          @cancel="cancelInvite"
        />
      </div>

      <p
        v-if="
          !canInvite &&
          attendees.some((a) => !a.is_registrant && !a.invite_status)
        "
        class="text-caption text-medium-emphasis mt-2"
      >
        Invites are no longer accepted for this event.
      </p>
    </template>
  </div>
</template>
